/**
 * Controller for map functionality
 */
import Map from 'ol/Map.js';
import View from 'ol/View.js';
import { fromLonLat } from 'ol/proj.js';
import { CONFIG } from '../config/config.js';
import { LayerFactory } from '../factories/layerFactory.js';
import { formatPopulation } from '../utils/formatter.js';
import { AppState } from './appState.js';

export class MapController {
  constructor() {
    this.currentCellSize = CONFIG.defaultCellSize;
    
    // Create layers
    this.basemapLayers = LayerFactory.createBasemapLayers();
    const watershedLayers = LayerFactory.createWatershedLayers(this.currentCellSize);
    this.boundaryLayers = watershedLayers.boundaryLayers;
    this.pointsLayers = watershedLayers.pointsLayers;
    
    // Create reference layers using the new method
    this.referenceLayers = LayerFactory.createReferenceLayers();
    
    this.popup = LayerFactory.createPopupOverlay();
    
    // Initialize map
    this.initializeMap();
    this.setupEventListeners();
  }
  
  /**
   * Initialize the OpenLayers map
   */
  initializeMap() {
    // Get all reference layers as an array
    const referenceLayersList = Object.values(this.referenceLayers).filter(layer => layer !== null);
    
    this.map = new Map({
      layers: [
        ...Object.values(this.basemapLayers),
        ...referenceLayersList,
        ...Object.values(this.boundaryLayers),
        ...Object.values(this.pointsLayers)
      ],
      overlays: [this.popup],
      target: 'map',
      view: new View({
        center: AppState.mapCenter,
        zoom: AppState.mapZoom
      })
    });
  }
  
  /**
   * Set up map event listeners
   */
  setupEventListeners() {
    // Close button functionality for popup
    const popupCloser = document.getElementById('popup-closer');
    if (popupCloser) {
      popupCloser.onclick = () => {
        this.popup.setPosition(undefined);
        popupCloser.blur();
        return false;
      };
    }
    
    // Map click handler
    this.map.on('singleclick', (evt) => this.handleMapClick(evt));
  }
  
  /**
   * Handle map click events
   */
  handleMapClick(evt) {
    // Don't show popups when animation is running
    if (AppState.isPlaying) {
      this.popup.setPosition(undefined);
      return;
    }
    
    // Get the visible points layers based on opacity sliders
    const visiblePointsLayers = CONFIG.sigmaValues.map(sigma => {
      return { 
        layer: this.pointsLayers[sigma], 
        sigma: sigma 
      };
    }).filter(item => item.layer.getVisible());
    
    // Sort by opacity, highest first (we want to query the most visible layer)
    visiblePointsLayers.sort((a, b) => b.layer.getOpacity() - a.layer.getOpacity());
    
    // Get the most visible layer (highest opacity)
    const activeLayerInfo = visiblePointsLayers.length > 0 ? visiblePointsLayers[0] : null;
    
    // Only proceed if any points layer is visible and has significant opacity (>= 0.5)
    if (!activeLayerInfo || activeLayerInfo.layer.getOpacity() < 0.5) {
      this.popup.setPosition(undefined);
      return;
    }
    
    const view = this.map.getView();
    const viewResolution = view.getResolution();
    const viewProjection = view.getProjection();
    
    const url = activeLayerInfo.layer.getSource().getFeatureInfoUrl(
      evt.coordinate,
      viewResolution,
      viewProjection,
      {
        'INFO_FORMAT': 'application/json',
        'FEATURE_COUNT': 1
      }
    );
    
    if (url) {
      fetch(url)
        .then(response => response.json())
        .then(data => {
          if (data.features && data.features.length > 0) {
            const feature = data.features[0];
            const nPois = feature.properties.n_pois;
            
            // Set popup content
            const popupContent = document.getElementById('popup-content');
            if (popupContent) {
              popupContent.innerHTML = `<p><strong>Cell Size:</strong> ${this.currentCellSize}</p>
                                       <p><strong>Sigma:</strong> ${activeLayerInfo.sigma}</p>
                                       <p><strong>POIs:</strong> ${nPois}</p>`;
              this.popup.setPosition(evt.coordinate);
            }
          } else {
            this.popup.setPosition(undefined);
          }
        })
        .catch(() => {
          this.popup.setPosition(undefined);
        });
    }
  }
  
  /**
   * Hide all basemap layers
   */
  hideAllBasemaps() {
    Object.values(this.basemapLayers).forEach(layer => {
      layer.setVisible(false);
    });
  }
  
  /**
   * Hide all reference layers
   */
  hideAllReferenceLayers() {
    Object.values(this.referenceLayers).forEach(layer => {
      if (layer) layer.setVisible(false);
    });
  }
  
  /**
   * Set the visibility of a specific reference layer
   * @param {string} layerKey - The key of the reference layer to show
   */
  setReferenceLayerVisibility(layerKey) {
    // First hide all reference layers
    this.hideAllReferenceLayers();
    
    // If 'none' is selected, we're done (all layers are hidden)
    if (layerKey === 'none') return;
    
    // Show the selected layer
    if (this.referenceLayers[layerKey]) {
      this.referenceLayers[layerKey].setVisible(true);
    }
  }
  
  /**
   * Set the visibility of a specific layer type
   * @param {string} layerType - The type of layer ('points' or 'boundary')
   * @param {boolean} visible - Whether the layer should be visible
   */
  setLayerVisibility(layerType, visible) {
    if (layerType === 'points') {
      // Toggle the visibility of all points layers
      Object.values(this.pointsLayers).forEach(layer => {
        // Only adjust visibility based on opacity - if opacity is 0, layer should stay invisible
        if (layer.getOpacity() > 0) {
          layer.setVisible(visible);
        }
      });
    } else if (layerType === 'boundary') {
      // Toggle the visibility of all boundary layers
      Object.values(this.boundaryLayers).forEach(layer => {
        // Only adjust visibility based on opacity - if opacity is 0, layer should stay invisible
        if (layer.getOpacity() > 0) {
          layer.setVisible(visible);
        }
      });
    }
    
    // Update UI controls to match
    if (layerType === 'points') {
      document.getElementById('points-toggle').checked = visible;
    } else if (layerType === 'boundary') {
      document.getElementById('boundary-toggle').checked = visible;
    }
    
    // Call updateAllLayersOpacity to ensure everything is in sync
    this.updateAllLayersOpacity();
  }
  
  /**
   * Update layer opacity and visibility
   */
  updateLayerOpacity(layer, opacity) {
    if (opacity > 0) {
      layer.setVisible(true);
      layer.setOpacity(opacity / 100);
    } else {
      layer.setVisible(false);
    }
  }
  
  /**
   * Update all layer opacities based on UI controls
   */
  updateAllLayersOpacity() {
    // Get checkbox status for layer types
    const boundaryToggle = document.getElementById('boundary-toggle');
    const pointsToggle = document.getElementById('points-toggle');
    
    // Process each sigma value
    CONFIG.sigmaValues.forEach(sigma => {
      const sigmaToggle = document.getElementById(`sigma-${sigma}-toggle`);
      const opacitySlider = document.getElementById(`opacity-${sigma}`);
      
      // Get opacity value for this sigma
      const opacity = parseInt(opacitySlider.value, 10);
      
      // Only show enabled sigma values
      const sigmaEnabled = sigmaToggle.checked;
      
      // Update boundary layer if toggle is enabled
      if (boundaryToggle && boundaryToggle.checked && sigmaEnabled) {
        this.updateLayerOpacity(this.boundaryLayers[sigma], opacity);
      } else {
        this.boundaryLayers[sigma].setVisible(false);
      }
      
      // Update points layer if toggle is enabled
      if (pointsToggle && pointsToggle.checked && sigmaEnabled) {
        this.updateLayerOpacity(this.pointsLayers[sigma], opacity);
      } else {
        this.pointsLayers[sigma].setVisible(false);
      }
    });
    
    // If POI filter is active, apply it to the visible layers
    const poiSlider = document.getElementById('poi-slider');
    const minPois = parseInt(poiSlider.value, 10);
    if (minPois > 0) {
      this.updatePointsFilter(minPois);
      this.updateBoundaryFilter(minPois);
    }
    
    // Update the sigma display in the header unless we're in animation mode
    if (!AppState.isPlaying) {
      const sigmaDisplay = document.getElementById('sigma-display');
      const activeSigma = AppState.getActiveSigma();
      sigmaDisplay.textContent = activeSigma;
    }
  }
  
  /**
   * Update points layer filter by POI count
   */
  updatePointsFilter(minPois = parseInt(document.getElementById('poi-slider').value, 10)) {
    Object.values(this.pointsLayers).forEach(layer => {
      if (layer.getVisible()) {
        const params = layer.getSource().getParams();
        if (minPois > 0) {
          params.CQL_FILTER = `n_pois >= ${minPois}`;
        } else {
          delete params.CQL_FILTER;
        }
        layer.getSource().updateParams(params);
      }
    });
  }
  
  /**
   * Update boundary layer filter by POI count
   */
  updateBoundaryFilter(minPois = parseInt(document.getElementById('poi-slider').value, 10)) {
    Object.values(this.boundaryLayers).forEach(layer => {
      if (layer.getVisible()) {
        const params = layer.getSource().getParams();
        if (minPois > 0) {
          params.CQL_FILTER = `n_pois >= ${minPois}`;
        } else {
          delete params.CQL_FILTER;
        }
        layer.getSource().updateParams(params);
      }
    });
  }
  
  /**
   * Search for a location using Nominatim
   * @param {string} query - The location to search for
   * @returns {Promise} - Promise resolving to search results
   */
  searchLocation(query) {
    const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}`;
    
    return fetch(url, {
      headers: {
        'Accept': 'application/json',
        'User-Agent': 'Cities as Inverted Watersheds Application'
      }
    })
    .then(response => {
      if (!response.ok) {
        throw new Error(`Failed to fetch location data: ${response.status} ${response.statusText}`);
      }
      return response.json();
    })
    .catch(error => {
      console.error('Location search failed:', error);
      // Re-throw a more user-friendly error message
      throw new Error('Unable to search for locations. Please check your network connection and try again.');
    });
  }
  
  /**
   * Zoom to a specific location based on its bounding box
   * @param {Object} location - The location object from Nominatim
   */
  zoomToLocation(location) {
    if (!location) return;
    
    // Check if the location has a bounding box
    if (location.boundingbox) {
      const bbox = [
        parseFloat(location.boundingbox[2]), // west/left
        parseFloat(location.boundingbox[0]), // south/bottom
        parseFloat(location.boundingbox[3]), // east/right
        parseFloat(location.boundingbox[1])  // north/top
      ];
      
      // Convert the bbox from WGS84 to the map's projection if needed
      const mapProjection = this.map.getView().getProjection();
      
      // Create the extent in the map's projection
      const extent = [
        fromLonLat([bbox[0], bbox[1]])[0],
        fromLonLat([bbox[0], bbox[1]])[1],
        fromLonLat([bbox[2], bbox[3]])[0],
        fromLonLat([bbox[2], bbox[3]])[1]
      ];
      
      // Fit the view to the bounding box with some padding
      this.map.getView().fit(extent, {
        padding: [50, 50, 50, 50],
        maxZoom: CONFIG.maxZoom
      });
    } else if (location.lat && location.lon) {
      // If there's no bounding box, center on the location with a default zoom
      const center = fromLonLat([parseFloat(location.lon), parseFloat(location.lat)]);
      this.map.getView().setCenter(center);
      this.map.getView().setZoom(10); // Default zoom level for when we don't have a bbox
    }
  }
  
  /**
   * Update layers based on cell size change
   * @param {number} cellSize - New cell size (100 or 1000)
   */
  updateCellSize(cellSize) {
    // Only update if the cell size has actually changed
    if (this.currentCellSize === cellSize) return;
    
    // Store current layer opacities before replacing layers
    const boundaryOpacities = {};
    const pointsOpacities = {};
    
    CONFIG.sigmaValues.forEach(sigma => {
      // Store current visibility and opacity for each layer
      boundaryOpacities[sigma] = {
        visible: this.boundaryLayers[sigma].getVisible(),
        opacity: this.boundaryLayers[sigma].getOpacity()
      };
      
      pointsOpacities[sigma] = {
        visible: this.pointsLayers[sigma].getVisible(),
        opacity: this.pointsLayers[sigma].getOpacity()
      };
      
      // Remove old layers from the map
      this.map.removeLayer(this.boundaryLayers[sigma]);
      this.map.removeLayer(this.pointsLayers[sigma]);
    });
    
    // Update current cell size
    this.currentCellSize = cellSize;
    
    // Create new watershed layers with updated cell size
    const watershedLayers = LayerFactory.createWatershedLayers(cellSize);
    this.boundaryLayers = watershedLayers.boundaryLayers;
    this.pointsLayers = watershedLayers.pointsLayers;
    
    // Add new layers to the map and restore visibility and opacity
    CONFIG.sigmaValues.forEach(sigma => {
      // Add new layers to the map
      this.map.addLayer(this.boundaryLayers[sigma]);
      this.map.addLayer(this.pointsLayers[sigma]);
      
      // Restore visibility and opacity
      if (boundaryOpacities[sigma]) {
        this.boundaryLayers[sigma].setVisible(boundaryOpacities[sigma].visible);
        this.boundaryLayers[sigma].setOpacity(boundaryOpacities[sigma].opacity);
      }
      
      if (pointsOpacities[sigma]) {
        this.pointsLayers[sigma].setVisible(pointsOpacities[sigma].visible);
        this.pointsLayers[sigma].setOpacity(pointsOpacities[sigma].opacity);
      }
    });
    
    // If population filter is active, re-apply it to the new layers
    const poiSlider = document.getElementById('poi-slider');
    const minPois = poiSlider ? parseInt(poiSlider.value, 10) : 0;
    if (minPois > 0) {
      this.updatePointsFilter(minPois);
      this.updateBoundaryFilter(minPois);
    }
    
    // Update the cell size in the app state
    AppState.cellSize = cellSize;
  }
}

export default MapController;